import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import AlertBanner from '../../../components/molecules/AlertBanner';
import FiscalResume from '../molecules/FiscalResume';
import Button from '../../../components/atoms/Button';
import { setSelectedPeriod } from '../../../redux/slices/taxCalendar.slice';
import { TaxboardDeclarationStatus } from '../../../@types/Calendar.type';
import { getMonthName } from '../../../shared/utils/dates.util';
import useCleverTapEvent from '../../../hooks/useClevertapEvent';
import {
	ANALYTICS_EVENTS,
	EVENT_TYPES,
} from '../../../shared/constants/events';
import { UserRegime } from '../../../@types/TaxProfile';
import { MonthlyDetail, Normal } from '../../../@types/MonthlyDetail.type';

interface Props {
	periodsAdquired: any[];
	monthlyDeclarationDetail: MonthlyDetail;
}

const getDeclarationType = (monthlyDetail: MonthlyDetail): string => {
	const { complementary } = monthlyDetail.declarations;
	return complementary && Array.isArray(complementary) && complementary.length > 0
		? 'Complementaria'
		: 'NORMAL';
};

const getTaxValues = (monthlyDetail: MonthlyDetail): { iva: number, isr: number, totalIncomes: number | string, totalExpenses: number | string, taxDeductible: number } => {
	if (monthlyDetail?.ffts && Array.isArray(monthlyDetail.ffts) && monthlyDetail.ffts.length > 0) {
		let targetFft;
		
		if (monthlyDetail.ffts.length === 1) {
			targetFft = monthlyDetail.ffts[0];
		} else {
			targetFft = monthlyDetail.ffts.find(fft => fft.regime === "PLA-TEC");
		}

		if (targetFft) {
			const isrTotal = targetFft.precalculation?.ISR?.reduce(
				(sum, item) => sum + (item.tax_retained || 0),
				0
			) || 0;

			const ivaValue = targetFft.precalculation?.IVA?.tax_retained ?? 0;

			return {
				isr: isrTotal,
				iva: ivaValue,
				totalIncomes: targetFft.total_income,
				totalExpenses: targetFft.total_expense,
				taxDeductible: targetFft.precalculation?.IVA?.deductions ?? 0
			};
		}
	}

	return {
		isr: monthlyDetail?.declarationCalculatedLegacy?.ISR?.tax_retention ?? 0,
		iva: monthlyDetail?.declarationCalculatedLegacy?.IVA?.tax_retention ?? 0,
		totalIncomes: monthlyDetail?.declarationCalculatedLegacy?.total_income ?? 0,
		totalExpenses: monthlyDetail?.declarationCalculatedLegacy?.total_expenses ?? 0,
		taxDeductible: monthlyDetail?.declarationCalculatedLegacy?.tax_deductible ?? 0
	};
};

const FiscalResumeLogic: React.FC<Props> = ({
	periodsAdquired,
	monthlyDeclarationDetail,
}) => {
	const logClevertapEvent = useCleverTapEvent();
	const dispatch = useDispatch();

	const { year, selectedPeriod, month, statusByPriotityMonth, fiscalResume } =
		useSelector((state: RootState) => state.taxCalendar);
	const { activeSuscriptions } = useSelector(
		(state: RootState) => state.profile,
	);
	const { userRegimes } = useSelector((state: RootState) => state.taxProfile);

	const { iva, isr, totalIncomes, totalExpenses, taxDeductible } = getTaxValues(monthlyDeclarationDetail);

	const getActiveDeclaration = (monthlyDetail: MonthlyDetail) => {
		if (!monthlyDetail?.declarations) return null;

		const { normal, complementary } = monthlyDetail.declarations;

		if (complementary && Array.isArray(complementary)) {
			if (complementary.length === 1) {
				return complementary[0];
			}
			if (complementary.length > 1) {
				return complementary.reduce((prev, current) =>
					prev.declaration.id > current.declaration.id ? prev : current
				);
			}
		}
		return normal;
	};

	const activeDeclaration: Normal = getActiveDeclaration(
		monthlyDeclarationDetail,
	);

	const statusBannerMonthly = () => {
		const variant: Record<
			string,
			'error' | 'success' | 'processing' | 'warning'
		> = {
			[TaxboardDeclarationStatus.PENDING]: 'error',
			[TaxboardDeclarationStatus.IN_PROCESS]: 'processing',
			[TaxboardDeclarationStatus.PRESENTED]: 'success',
			[TaxboardDeclarationStatus.PENDING_PAY]: 'warning',
		};

		const title: Record<string, string> = {
			[TaxboardDeclarationStatus.PENDING]: 'Tienes declaraciones pendientes',
			[TaxboardDeclarationStatus.IN_PROCESS]:
				'La información estará disponible en 10 días',
			[TaxboardDeclarationStatus.PRESENTED]: `Estás al día en tus declaraciones de ${getMonthName(month)} de ${year}`,
			[TaxboardDeclarationStatus.PENDING_PAY]: `Tienes declaraciones pendientes de pago`,
			['ACQUIRED']: `Periodo adquirido`, //TODO: Change this copy Stive
		};

		const description: Record<string, string> = {
			[TaxboardDeclarationStatus.PENDING]:
				'Presenta todas tus declaraciones pendientes y evita multas o sanciones.',
			[TaxboardDeclarationStatus.IN_PROCESS]:
				'Recuerda presentar puntualmente tus declaraciones con Heru.',
			[TaxboardDeclarationStatus.PRESENTED]:
				activeSuscriptions.length > 0
					? 'Recuerda presentar puntualmente tus declaraciones para mantener tu estatus al día.'
					: 'Suscríbete a los servicios de Heru y presenta puntualmente tus declaraciones.',
			[TaxboardDeclarationStatus.PENDING_PAY]:
				activeSuscriptions.length > 0
					? 'Apresúrate a hacer tus pagos con tus líneas de captura.'
					: 'Apresúrate a hacer tus pagos con tus líneas de captura. Recuerda suscribirte a los servicios de Heru para mantenerte al día.',
			['ACQUIRED']: `Periodo adquirido`, //TODO: Change this copy Stive
		};

		if (
			periodsAdquired?.length > 0 &&
			!!periodsAdquired?.find(
				(period: any) => period.month === month && period.year === year,
			) &&
			statusByPriotityMonth === TaxboardDeclarationStatus.PENDING
		) {
			return (
				<AlertBanner
					variant='info'
					title={title['ACQUIRED']}
					description={description['ACQUIRED']}
				/>
			);
		}
		return (
			<AlertBanner
				variant={variant[statusByPriotityMonth]}
				title={title[statusByPriotityMonth]}
				description={description[statusByPriotityMonth]}
			/>
		);
	};

	const handleCloseRegimeDetail = () => {
		dispatch(
			setSelectedPeriod({
				year: {
					...selectedPeriod.year,
					selectedDeclaration: null,
				},
				month: {
					...selectedPeriod.month,
					detailByRegime: null,
				},
			}),
		);
	};

	const handleDownloadReceipt = () => {
		logClevertapEvent(ANALYTICS_EVENTS.TAX_DASHBOARD, {
			event_type: EVENT_TYPES.TAXBOARD_TAX_RECEIPT_DOWNLOADED,
			tax_detail: `${selectedPeriod?.month?.detailByRegime?.month || ''}/${selectedPeriod?.month?.detailByRegime?.year || ''}`,
			regime:
				userRegimes?.map((regime: UserRegime) => regime?.name).join(' - ') ||
				'[]',
		});

		window.open(activeDeclaration?.declaration?.url, '_blank');
	};

	const getPresentedBy = (month?: number, year?: number) => {
		const periodAdquired = periodsAdquired.find(
			(period: any) => period.month === month && period.year === year
		);
		
		return periodAdquired ? 'Heru' : 'Presentado por usuario';
	};

	return (
		<FiscalResume
			title={`Resumen de la declaración`}
			year={year}
			month={month}
			description={
				<>
					Esta es la información de tu declaración presentada de{' '}
					{
						<span className='font-bold'>
							Plataformas tecnológicas de {getMonthName(month)} de {year}.
						</span>
					}
				</>
			}
			incomes={Number(totalIncomes)}
			expenses={Number(totalExpenses)}
			retentions={fiscalResume.month?.retentions}
			iva={iva}
			isr={isr}
			deductibleExpenses={taxDeductible}
			tagText='Plataformas Tecnológicas'
			onTagClick={handleCloseRegimeDetail}
			amountToPay={activeDeclaration?.declaration?.amount ?? 0}
			presentedDate={activeDeclaration?.declaration?.presented_at ?? 'Sin datos'}
			declarationType={getDeclarationType(monthlyDeclarationDetail)}
			presentedBy={getPresentedBy(month, year)}
			showRefresh
			banner={statusBannerMonthly()}
			cta={
				<Button
					label='Descargar acuse'
					onClick={handleDownloadReceipt}
				/>
			}
		/>
	);
};

export default FiscalResumeLogic;
